/**
 * useSheetData — primary data-fetch hook (v3)
 *
 * Consumes AuthContext for the active SheetSource (chosen by the user) and
 * fetches its single flat tab via the public GViz CSV endpoint. No token is
 * required — the sheet is shared as "Anyone with the link can view".
 *
 * Auto-refresh polls on an interval and re-fetches when the tab regains
 * visibility, so the dashboard stays live without a manual reload.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { type FetchResult } from '@/api/sheetsClient';
import { fetchTelemetryAsync } from '@/api/telemetryClient';
import { useAuth } from '@/auth/AuthContext';
import { REFRESH_MS } from '@/config/app';
import type { TelemetryRow } from '@/types';

export type LoadStatus = 'idle' | 'loading' | 'refreshing' | 'success' | 'error';

export interface UseSheetData {
  rows:      TelemetryRow[];
  fetchedAt: Date | null;
  status:    LoadStatus;
  error:     string | null;
  counts:    FetchResult['counts'] | null;
  refetch:   () => Promise<void>;
}

export function useSheetData(): UseSheetData {
  const { selectedSheet } = useAuth();

  const [rows,      setRows]      = useState<TelemetryRow[]>([]);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  const [status,    setStatus]    = useState<LoadStatus>('idle');
  const [error,     setError]     = useState<string | null>(null);
  const [counts,    setCounts]    = useState<FetchResult['counts'] | null>(null);

  const hasData  = useRef(false);
  // Monotonic request id: every fetch tags itself, and a result is only applied
  // if it is still the latest request. Switching meters (or a slow poll landing
  // after a switch) bumps this, so a stale meter's data can never flash in.
  const reqId    = useRef(0);
  const inFlight = useRef(false);
  const timer    = useRef<number | null>(null);

  const load = useCallback(async (mode: 'initial' | 'refresh' = 'refresh') => {
    if (!selectedSheet) return;
    // Don't stack polls on top of an in-flight fetch (avoids hammering GViz →
    // HTTP 429). A meter switch uses mode 'initial' and always proceeds so it is
    // never dropped; reqId below discards whichever result is no longer current.
    if (mode === 'refresh' && inFlight.current) return;

    const myReq = ++reqId.current;
    inFlight.current = true;
    setStatus(mode === 'initial' ? 'loading' : 'refreshing');

    try {
      const result = await fetchTelemetryAsync(selectedSheet);
      if (myReq !== reqId.current) return; // superseded (meter switched) — drop
      hasData.current = true;
      setRows(result.rows);
      setFetchedAt(result.fetchedAt);
      setCounts(result.counts);
      setStatus('success');
      setError(null);
    } catch (err) {
      if (myReq !== reqId.current) return; // stale failure — ignore
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      setStatus(hasData.current ? 'success' : 'error');
      console.error('❌ Telemetry fetch failed:', msg);
    } finally {
      if (myReq === reqId.current) inFlight.current = false;
    }
  }, [selectedSheet]);

  // Initial load + re-load when the selected sheet changes
  useEffect(() => {
    if (!selectedSheet) return;
    hasData.current = false;
    setRows([]);
    void load('initial');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSheet?.id]);

  // Polling
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') void load('refresh'); };
    timer.current = window.setInterval(tick, REFRESH_MS);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load]);

  // Refetch on visibility restore
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible' && fetchedAt) {
        if (Date.now() - fetchedAt.getTime() > REFRESH_MS / 2) void load('refresh');
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load, fetchedAt]);

  return { rows, fetchedAt, status, error, counts, refetch: () => load('refresh') };
}
